import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, MoreVertical } from 'lucide-react';
import {
  AGENT_SESSION_STATUS,
  formatSessionActivity,
  resolveSessionDisplayStatus,
} from '../utils/agentSessionStorage';

function SessionStatusBadge({ status, p }) {
  const STATUS_META = {
    [AGENT_SESSION_STATUS.SHARED]: {
      label: p('status_shared'),
      className: 'bg-teal-500/15 text-teal-300 border-teal-400/25',
    },
    [AGENT_SESSION_STATUS.IN_PROGRESS]: {
      label: p('status_in_progress'),
      className: 'bg-blue-500/15 text-blue-300 border-blue-400/25',
    },
    [AGENT_SESSION_STATUS.PRIVATE]: {
      label: p('status_private'),
      className: 'bg-violet-500/15 text-violet-300 border-violet-400/25',
    },
  };
  const meta = STATUS_META[status] || STATUS_META[AGENT_SESSION_STATUS.PRIVATE];
  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${meta.className}`}
    >
      {meta.label}
    </span>
  );
}

function SessionRowMenu({ session, onOpen, onToggleShare, onDelete, p }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const isShared = !!session.shared;

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        aria-label={p('actions_aria')}
        onClick={() => setOpen((value) => !value)}
        className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors"
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-20 min-w-[180px] rounded-xl border border-white/15 bg-slate-900/95 backdrop-blur-xl shadow-2xl overflow-hidden">
          <button
            type="button"
            className="w-full text-left px-3 py-2.5 text-sm text-white/90 hover:bg-white/10"
            onClick={() => {
              setOpen(false);
              onOpen?.(session.id);
            }}
          >
            {p('open')}
          </button>
          <button
            type="button"
            className="w-full text-left px-3 py-2.5 text-sm text-white/90 hover:bg-white/10"
            onClick={() => {
              setOpen(false);
              onToggleShare?.(session.id);
            }}
          >
            {isShared ? p('make_private') : p('share')}
          </button>
          <button
            type="button"
            className="w-full text-left px-3 py-2.5 text-sm text-red-300 hover:bg-red-500/10"
            onClick={() => {
              setOpen(false);
              onDelete?.(session.id);
            }}
          >
            {p('delete')}
          </button>
        </div>
      )}
    </div>
  );
}

export default function AIAgentRecentSessions({
  sessions = [],
  activeSessionId,
  onOpenSession,
  onToggleSessionShare,
  onDeleteSession,
  showWhenEmpty = false,
}) {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.shared.aiAgent.recentSessions.${key}`, opts);

  if (!sessions.length && !showWhenEmpty) return null;

  return (
    <section className="mx-auto w-full max-w-4xl mb-4">
      <div className="rounded-3xl border border-white/15 bg-gradient-to-br from-white/[0.08] via-blue-950/20 to-indigo-950/30 backdrop-blur-lg overflow-hidden">
        <div className="px-5 sm:px-6 py-4 border-b border-white/10 flex items-center gap-3">
          <Clock className="h-5 w-5 text-white/70 shrink-0" />
          <h2 className="m-0 text-base sm:text-lg font-semibold text-white">{p('title')}</h2>
        </div>

        {sessions.length === 0 ? (
          <p className="m-0 px-5 sm:px-6 py-8 text-sm text-white/45 text-center">
            {p('empty')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse">
              <thead>
                <tr className="text-left text-xs sm:text-sm text-white/45">
                  <th className="px-5 sm:px-6 py-3 font-medium">{p('col_session')}</th>
                  <th className="px-3 py-3 font-medium">{p('col_status')}</th>
                  <th className="px-3 py-3 font-medium">{p('col_last_activity')}</th>
                  <th className="px-3 py-3 font-medium">{p('col_owner')}</th>
                  <th className="px-3 py-3 w-12" aria-label={p('col_actions')} />
                </tr>
              </thead>
              <tbody>
                {sessions.map((session, index) => {
                  const isActive = session.id === activeSessionId;
                  return (
                    <tr
                      key={session.id}
                      className={`group transition-colors ${
                        index < sessions.length - 1 ? 'border-b border-white/[0.06]' : ''
                      } ${isActive ? 'bg-white/[0.04]' : 'hover:bg-white/[0.03]'}`}
                    >
                      <td className="px-5 sm:px-6 py-4">
                        <button
                          type="button"
                          onClick={() => onOpenSession?.(session.id)}
                          className="text-left text-sm sm:text-[0.95rem] font-medium text-white hover:text-blue-200 transition-colors"
                        >
                          {session.title}
                        </button>
                      </td>
                      <td className="px-3 py-4">
                        <SessionStatusBadge
                          status={resolveSessionDisplayStatus(session, activeSessionId)}
                          p={p}
                        />
                      </td>
                      <td className="px-3 py-4 text-sm text-white/55 whitespace-nowrap">
                        {formatSessionActivity(session.updatedAt)}
                      </td>
                      <td className="px-3 py-4 text-sm text-white/70 whitespace-nowrap">
                        {session.owner}
                      </td>
                      <td className="px-3 py-4 text-right">
                        <SessionRowMenu
                          session={session}
                          onOpen={onOpenSession}
                          onToggleShare={onToggleSessionShare}
                          onDelete={onDeleteSession}
                          p={p}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
